import { motion } from "framer-motion";
import { useT } from "@/i18n/I18nContext";
import { BRAND_LOGOS } from "@/data/logos";

export default function TrustedBy() {
  const { t } = useT();

  return (
    <section
      id="trusted"
      data-testid="trusted-section"
      className="relative border-y border-[var(--tt-border)] py-16 md:py-20"
    >
      <div className="mx-auto max-w-[1480px] px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl"
        >
          {t("trusted.overline") ? (
            <p className="tt-overline">{t("trusted.overline")}</p>
          ) : null}
          <h2 className="mt-4 font-serif text-2xl md:text-3xl leading-[1.15] text-[var(--tt-text)]">
            {t("trusted.title")}
          </h2>
          <p className="mt-4 text-[var(--tt-text-dim)] font-light leading-relaxed max-w-2xl text-sm md:text-base">
            {t("trusted.description")}
          </p>
        </motion.div>

        {/* Premium typographic wordmarks — Apple / Stripe / Linear style */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 md:mt-14"
        >
          {/* Desktop / tablet: single row, centered, equal spacing */}
          <ul className="hidden sm:flex flex-wrap items-center justify-between gap-y-10 gap-x-6">
            {BRAND_LOGOS.map((b, i) => (
              <li
                key={b.name}
                data-testid={`trusted-logo-${b.name.toLowerCase()}`}
                className="flex-1 min-w-[130px] flex items-center justify-center"
              >
                <LogoMark brand={b} index={i} />
              </li>
            ))}
          </ul>

          {/* Mobile: horizontal scroll, centered */}
          <div className="sm:hidden -mx-6">
            <ul
              className="flex items-center gap-12 px-6 overflow-x-auto snap-x snap-mandatory"
              style={{ scrollbarWidth: "none" }}
            >
              {BRAND_LOGOS.map((b, i) => (
                <li
                  key={b.name}
                  className="snap-center shrink-0 flex items-center"
                  data-testid={`trusted-logo-mobile-${b.name.toLowerCase()}`}
                >
                  <LogoMark brand={b} index={i} />
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        {/* Bottom caption */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.5 }}
          className="mt-12 md:mt-14 text-center text-[var(--tt-text-muted)] font-light italic text-sm max-w-2xl mx-auto leading-relaxed"
        >
          {t("trusted.caption")}
        </motion.p>
      </div>
    </section>
  );
}

function LogoMark({ brand, index }) {
  return (
    <motion.img
      src={brand.file}
      alt={brand.name}
      loading="lazy"
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 0.55, y: 0 }}
      viewport={{ once: true }}
      transition={{
        opacity: { duration: 0.6, delay: 0.15 + index * 0.06 },
        y: { duration: 0.5, delay: 0.15 + index * 0.06 },
      }}
      whileHover={{ opacity: 1 }}
      style={{ height: brand.h, width: "auto" }}
      className="select-none object-contain transition-opacity duration-500"
      draggable={false}
    />
  );
}
